import type {
  ZCriterionNode,
  ZGroupNode,
  ZNodePath,
  ZQueryBuilderNode,
} from "../types/ZQueryBuilderTypes";
import {
  canMoveDown as utilCanMoveDown,
  canMoveUp as utilCanMoveUp,
  findNodeByPath,
  getParentGroup,
  isGroupNode,
} from "./treeUtils";

type MoveDirection = -1 | 1;

type MoveCapability = {
  canMoveUp: boolean;
  canMoveDown: boolean;
};

type MoveComputationContext = {
  criterionIndexById: Map<string, number>;
  criterionCount: number;
};

const findCriterionNode = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
): ZCriterionNode | null => {
  const criterion = findNodeByPath(rootGroup, criterionPath);

  if (!criterion || isGroupNode(criterion)) {
    return null;
  }

  return criterion;
};

const removeItemAtPath = (rootGroup: ZGroupNode, itemPath: ZNodePath): void => {
  if (itemPath.length === 0) {
    return;
  }

  const parent = getParentGroup(rootGroup, itemPath);
  if (!parent) {
    return;
  }

  const index = itemPath[itemPath.length - 1];
  if (index === undefined) {
    return;
  }

  parent.items.splice(index, 1);
};

const swapNode = (
  rootGroup: ZGroupNode,
  nodePath: ZNodePath,
  delta: MoveDirection,
): void => {
  const parent = getParentGroup(rootGroup, nodePath);
  if (!parent) {
    return;
  }

  const index = nodePath[nodePath.length - 1];
  if (index === undefined) {
    return;
  }

  const a = parent.items[index];
  const b = parent.items[index + delta];
  if (!a || !b) {
    return;
  }

  parent.items[index] = b;
  parent.items[index + delta] = a;
};

const collectCriterionTraversal = (rootGroup: ZGroupNode): string[] => {
  const result: string[] = [];

  const walkGroup = (group: ZGroupNode): void => {
    group.items.forEach((item) => {
      if (isGroupNode(item)) {
        walkGroup(item);
        return;
      }

      result.push(item.id);
    });
  };

  walkGroup(rootGroup);
  return result;
};

const buildMoveComputationContext = (
  rootGroup: ZGroupNode,
): MoveComputationContext => {
  const traversal = collectCriterionTraversal(rootGroup);
  const criterionIndexById = new Map<string, number>();

  traversal.forEach((id, index) => {
    criterionIndexById.set(id, index);
  });

  return {
    criterionIndexById,
    criterionCount: traversal.length,
  };
};

const getTraversalTargetPath = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): ZNodePath | null => {
  const criterion = findCriterionNode(rootGroup, criterionPath);
  if (!criterion) {
    return null;
  }

  const traversal = collectCriterionTraversal(rootGroup);
  const currentIndex = traversal.indexOf(criterion.id);
  if (currentIndex < 0) {
    return null;
  }

  const targetCriterionId = traversal[currentIndex + direction];
  if (!targetCriterionId) {
    return null;
  }

  return findPathByNodeId(rootGroup, targetCriterionId);
};

const findPathByNodeId = (
  rootGroup: ZGroupNode,
  nodeId: string,
): ZNodePath | null => {
  const walk = (node: ZQueryBuilderNode, path: ZNodePath): ZNodePath | null => {
    if (node.id === nodeId) {
      return path;
    }

    if (!isGroupNode(node)) {
      return null;
    }

    for (let index = 0; index < node.items.length; index++) {
      const child = node.items[index];
      if (!child) {
        continue;
      }

      const result = walk(child, [...path, index]);
      if (result) {
        return result;
      }
    }

    return null;
  };

  return walk(rootGroup, []);
};

const getAdjacentGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): ZGroupNode | null => {
  const parent = getParentGroup(rootGroup, criterionPath);
  if (!parent) {
    return null;
  }

  const index = criterionPath[criterionPath.length - 1];
  if (index === undefined) {
    return null;
  }

  const adjacent = parent.items[index + direction];
  return adjacent && isGroupNode(adjacent) ? adjacent : null;
};

const canMoveCriterionIntoAdjacentGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): boolean => getAdjacentGroup(rootGroup, criterionPath, direction) !== null;

const moveCriterionIntoAdjacentGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): void => {
  const criterion = findCriterionNode(rootGroup, criterionPath);
  if (!criterion) {
    return;
  }

  const adjacent = getAdjacentGroup(rootGroup, criterionPath, direction);
  if (!adjacent) {
    return;
  }

  removeItemAtPath(rootGroup, criterionPath);

  if (direction === 1) {
    adjacent.items.unshift(criterion);
    return;
  }

  adjacent.items.push(criterion);
};

const canMoveCriterionOutOfGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): boolean => {
  const parent = getParentGroup(rootGroup, criterionPath);
  if (!parent) {
    return false;
  }

  const index = criterionPath[criterionPath.length - 1];
  if (index === undefined) {
    return false;
  }

  const parentPath = criterionPath.slice(0, -1);
  if (parentPath.length === 0) {
    return false;
  }

  const grandParent = getParentGroup(rootGroup, parentPath);
  if (!grandParent) {
    return false;
  }

  if (direction === -1) {
    return index === 0;
  }

  return index === parent.items.length - 1;
};

const moveCriterionOutOfGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): boolean => {
  if (!canMoveCriterionOutOfGroup(rootGroup, criterionPath, direction)) {
    return false;
  }

  const criterion = findCriterionNode(rootGroup, criterionPath);
  if (!criterion) {
    return false;
  }

  const parentPath = criterionPath.slice(0, -1);
  const parentNode = findNodeByPath(rootGroup, parentPath);
  if (!parentNode || !isGroupNode(parentNode)) {
    return false;
  }

  removeItemAtPath(rootGroup, criterionPath);

  const updatedParentPath = findPathByNodeId(rootGroup, parentNode.id);
  if (!updatedParentPath) {
    return false;
  }

  const updatedGrandParent = getParentGroup(rootGroup, updatedParentPath);
  if (!updatedGrandParent) {
    return false;
  }

  const updatedParentIndex = updatedParentPath[updatedParentPath.length - 1];
  if (updatedParentIndex === undefined) {
    return false;
  }

  const insertIndex =
    direction === -1 ? updatedParentIndex : updatedParentIndex + 1;
  updatedGrandParent.items.splice(insertIndex, 0, criterion);
  return true;
};

const moveCriterionIntoTraversalTargetGroup = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): boolean => {
  if (!canMoveCriterionOutOfGroup(rootGroup, criterionPath, direction)) {
    return false;
  }

  const criterion = findCriterionNode(rootGroup, criterionPath);
  if (!criterion) {
    return false;
  }

  const targetPath = getTraversalTargetPath(
    rootGroup,
    criterionPath,
    direction,
  );
  // targetPath.length === 1 means the target criterion is a direct child of
  // rootGroup — not nested in any sub-group — so there is nothing to join.
  const isDirectChildOfRoot = !targetPath || targetPath.length < 2;
  if (isDirectChildOfRoot) {
    return false;
  }

  const targetParent = getParentGroup(rootGroup, targetPath);
  const targetPosition = targetPath[targetPath.length - 1];
  if (!targetParent || targetPosition === undefined) {
    return false;
  }

  // targetParent is in a different branch from criterionPath, so removing the
  // criterion does not shift targetParent's position. Insert directly.
  removeItemAtPath(rootGroup, criterionPath);

  const insertIndex = direction === -1 ? targetPosition + 1 : targetPosition;
  targetParent.items.splice(insertIndex, 0, criterion);
  return true;
};

const moveCriterionByTraversal = (
  rootGroup: ZGroupNode,
  criterionPath: ZNodePath,
  direction: MoveDirection,
): void => {
  const criterion = findCriterionNode(rootGroup, criterionPath);
  if (!criterion) {
    return;
  }

  if (
    moveCriterionIntoTraversalTargetGroup(rootGroup, criterionPath, direction)
  ) {
    return;
  }

  if (moveCriterionOutOfGroup(rootGroup, criterionPath, direction)) {
    return;
  }

  if (canMoveCriterionIntoAdjacentGroup(rootGroup, criterionPath, direction)) {
    moveCriterionIntoAdjacentGroup(rootGroup, criterionPath, direction);
    return;
  }

  const targetPath = getTraversalTargetPath(
    rootGroup,
    criterionPath,
    direction,
  );
  if (!targetPath) {
    return;
  }

  const sourceParentPath = criterionPath.slice(0, -1);
  const targetParentPath = targetPath.slice(0, -1);
  const sourcePosition = criterionPath[criterionPath.length - 1];
  const targetPosition = targetPath[targetPath.length - 1];
  if (sourcePosition === undefined || targetPosition === undefined) {
    return;
  }

  removeItemAtPath(rootGroup, criterionPath);

  const targetParent = getParentGroup(rootGroup, targetPath);
  if (!targetParent) {
    return;
  }

  const isSameParent =
    sourceParentPath.length === targetParentPath.length &&
    sourceParentPath.every((item, index) => item === targetParentPath[index]);
  let updatedTargetPosition = targetPosition;
  if (isSameParent && sourcePosition < targetPosition) {
    updatedTargetPosition -= 1;
  }

  const insertIndex =
    direction === -1 ? updatedTargetPosition : updatedTargetPosition + 1;
  targetParent.items.splice(insertIndex, 0, criterion);
};

const canMoveNodeByDirectionWithContext = (
  rootGroup: ZGroupNode,
  nodePath: ZNodePath,
  direction: MoveDirection,
  context: MoveComputationContext,
): boolean => {
  const criterion = findCriterionNode(rootGroup, nodePath);
  if (!criterion) {
    return direction === -1
      ? utilCanMoveUp(rootGroup, nodePath)
      : utilCanMoveDown(rootGroup, nodePath);
  }

  const currentIndex = context.criterionIndexById.get(criterion.id);
  if (currentIndex === undefined) {
    return false;
  }

  const hasSiblingCriterion =
    direction === -1
      ? currentIndex > 0
      : currentIndex < context.criterionCount - 1;

  return (
    hasSiblingCriterion ||
    canMoveCriterionOutOfGroup(rootGroup, nodePath, direction) ||
    canMoveCriterionIntoAdjacentGroup(rootGroup, nodePath, direction)
  );
};

export const canMoveNodeByDirection = (
  rootGroup: ZGroupNode,
  nodePath: ZNodePath,
  direction: MoveDirection,
): boolean =>
  canMoveNodeByDirectionWithContext(
    rootGroup,
    nodePath,
    direction,
    buildMoveComputationContext(rootGroup),
  );

export const getChildrenMoveCapabilities = (
  rootGroup: ZGroupNode,
  parentPath: ZNodePath,
): MoveCapability[] => {
  const parent = findNodeByPath(rootGroup, parentPath);
  if (!parent || !isGroupNode(parent)) {
    return [];
  }

  const context = buildMoveComputationContext(rootGroup);

  return parent.items.map((_, index) => {
    const childPath = [...parentPath, index];

    return {
      canMoveUp: canMoveNodeByDirectionWithContext(
        rootGroup,
        childPath,
        -1,
        context,
      ),
      canMoveDown: canMoveNodeByDirectionWithContext(
        rootGroup,
        childPath,
        1,
        context,
      ),
    };
  });
};

export const moveNodeByDirection = (
  rootGroup: ZGroupNode,
  nodePath: ZNodePath,
  direction: MoveDirection,
): void => {
  if (findCriterionNode(rootGroup, nodePath)) {
    moveCriterionByTraversal(rootGroup, nodePath, direction);
    return;
  }

  if (canMoveNodeByDirection(rootGroup, nodePath, direction)) {
    swapNode(rootGroup, nodePath, direction);
  }
};
